// src/components/useSheetPull.js
//
// One gesture for every bottom sheet in the app. The rule is the one iOS
// sheets follow: with the content at its top (or a touch on the chrome), a
// downward drag moves the sheet; otherwise it scrolls the content. The
// decision is made on the first move and held for the rest of the gesture.
//
// Native listeners, not React's — React registers touch handlers as passive,
// so a drag could never cancel the scroll under it. The entrance animation
// fills forwards and an animation value outranks an inline style, so the fill
// is released on touchstart or the sheet would sit still under the finger.

import { useEffect } from "react";

export function useSheetPull({ sheetRef, bodyRef, overlayRef, closeRef, dirtyRef }) {
  // The page behind must not move while the sheet is up.
  useEffect(() => {
    document.body.classList.add("pkOpen");
    return () => document.body.classList.remove("pkOpen");
  }, []);

  useEffect(() => {
    const el = sheetRef.current;
    if (!el) return undefined;
    const d = { y0: null, x0: 0, dy: 0, on: false, decided: false, inBody: false, atTop: true, v: 0, lastY: 0, lastT: 0 };
    const scrim = () => overlayRef.current;
    const settle = (closing) => {
      const ease = "cubic-bezier(0.32, 0.72, 0, 1)";
      el.style.transition = `transform 260ms ${ease}`;
      if (scrim()) scrim().style.transition = "opacity 220ms ease";
      el.style.transform = closing ? "translateY(110%)" : "translateY(0)";
      if (scrim()) scrim().style.opacity = closing ? "0" : "1";
    };
    const start = (e) => {
      const t = e.touches[0];
      const body = bodyRef.current;
      const inBody = !!(body && body.contains(e.target));
      // A note that has scrolled inside its own field is scrolled, too; and a
      // picker wheel is a scroller in its own right — a finger on it is never
      // a pull.
      const field = e.target && e.target.closest ? e.target.closest("textarea") : null;
      const wheel = e.target && e.target.closest ? e.target.closest(".gwWcol") : null;
      const atTop = !inBody || (body.scrollTop <= 0 && !(field && field.scrollTop > 0) && !wheel);
      Object.assign(d, { y0: t.clientY, x0: t.clientX, dy: 0, on: false, decided: false, inBody, atTop, lastY: t.clientY, lastT: Date.now(), v: 0 });
      el.style.animation = "none";
      if (scrim()) scrim().style.animation = "none";
      el.style.transition = "none";
      if (scrim()) scrim().style.transition = "none";
    };
    const move = (e) => {
      if (d.y0 == null) return;
      const t = e.touches[0];
      const dy = t.clientY - d.y0;
      const dx = t.clientX - d.x0;
      if (!d.decided) {
        // iOS honours preventDefault only on the FIRST touchmove of a
        // gesture. At the top, the first downward move claims it outright.
        if (d.atTop && dy > 0 && Math.abs(dy) >= Math.abs(dx)) { d.decided = true; d.on = true; }
        else if (Math.abs(dy) >= 6 || Math.abs(dx) >= 6) { d.decided = true; d.on = false; }
        else return;
      }
      if (!d.on) {
        // Not a pull: the body may scroll, but the chrome never pans.
        if (!d.inBody && e.cancelable) e.preventDefault();
        return;
      }
      if (e.cancelable) e.preventDefault();
      const now = Date.now();
      d.v = (t.clientY - d.lastY) / Math.max(1, now - d.lastT);
      d.lastY = t.clientY; d.lastT = now;
      d.dy = Math.max(0, dy);
      el.style.transform = `translateY(${d.dy}px)`;
      if (scrim()) scrim().style.opacity = String(Math.max(0.2, 1 - d.dy / 700));
    };
    const end = () => {
      if (d.y0 == null) return;
      const h = el.getBoundingClientRect().height || 500;
      // A flick closes it; so does dragging past a quarter of its height.
      // Not with unsaved edits: a sheet with a draft snaps back.
      const flick = d.v > 0.5 && d.dy > 30;
      const dirty = !!(dirtyRef && dirtyRef.current);
      const closing = d.on && !dirty && (flick || d.dy > Math.min(140, h * 0.25));
      settle(closing);
      if (closing) setTimeout(() => closeRef.current && closeRef.current(), 220);
      Object.assign(d, { y0: null, dy: 0, on: false, decided: false });
    };
    const cancel = () => {
      if (d.y0 == null) return;
      settle(false);
      Object.assign(d, { y0: null, dy: 0, on: false, decided: false });
    };
    el.addEventListener("touchstart", start, { passive: true });
    el.addEventListener("touchmove", move, { passive: false });
    el.addEventListener("touchend", end);
    el.addEventListener("touchcancel", cancel);
    return () => {
      el.removeEventListener("touchstart", start);
      el.removeEventListener("touchmove", move);
      el.removeEventListener("touchend", end);
      el.removeEventListener("touchcancel", cancel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
